import React, { useState, useEffect } from 'react';
import { Tag, Plus, Edit2, Trash2, Check, X, RefreshCw, Copy } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_OPTIONS = ['Basic', 'Advanced', 'Professional'];

const emptyForm = {
  code: '',
  title: '',
  description: '',
  type: 'fixed',
  discountedPrice: 99,
  appliesTo: ['Basic'],
  active: true
};

const CouponsManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null); // null = create mode
  const [form, setForm] = useState(emptyForm);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/coupons`);
      setCoupons(res.data || []);
    } catch (e) {
      toast({ title: 'Failed to load coupons', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoupons(); }, []);

  const openCreate = () => {
    setEditingCode(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (coupon) => {
    setEditingCode(coupon.code);
    setForm({
      code: coupon.code,
      title: coupon.title || '',
      description: coupon.description || '',
      type: coupon.type || 'fixed',
      discountedPrice: coupon.discountedPrice || 0,
      appliesTo: coupon.appliesTo || ['Basic'],
      active: coupon.active !== false
    });
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingCode(null);
    setForm(emptyForm);
  };

  const togglePlan = (plan) => {
    setForm((p) => {
      const exists = p.appliesTo.includes(plan);
      return {
        ...p,
        appliesTo: exists ? p.appliesTo.filter((x) => x !== plan) : [...p.appliesTo, plan]
      };
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.code.trim()) {
      toast({ title: 'Coupon code is required', variant: 'destructive' });
      return;
    }
    if (!form.appliesTo || form.appliesTo.length === 0) {
      toast({ title: 'Select at least one plan', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        title: form.title.trim(),
        description: form.description.trim(),
        type: form.type,
        discountedPrice: Number(form.discountedPrice),
        appliesTo: form.appliesTo,
        active: !!form.active
      };
      if (editingCode) {
        await axios.put(`${API}/admin/coupons/${editingCode}`, payload);
        toast({ title: '✅ Coupon updated' });
      } else {
        await axios.post(`${API}/admin/coupons`, payload);
        toast({ title: '✅ Coupon created' });
      }
      cancelForm();
      fetchCoupons();
    } catch (e) {
      toast({
        title: 'Save failed',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      await axios.put(`${API}/admin/coupons/${coupon.code}`, { active: !coupon.active });
      toast({ title: coupon.active ? 'Coupon disabled' : '✅ Coupon enabled' });
      fetchCoupons();
    } catch (e) {
      toast({ title: 'Toggle failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm(`Delete coupon ${coupon.code}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/coupons/${coupon.code}`);
      toast({ title: 'Coupon deleted' });
      fetchCoupons();
    } catch (e) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast({ title: `Copied: ${code}` });
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Tag className="w-5 h-5 md:w-6 md:h-6 text-orange-500" />
            Coupons & Offers
          </h2>
          <p className="text-xs md:text-sm text-slate-500 mt-1">
            Run campaigns by creating discount coupon codes for any plan.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchCoupons} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {!showForm && (
            <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Plus className="w-4 h-4 mr-1.5" />
              New Coupon
            </Button>
          )}
        </div>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="text-base md:text-lg text-slate-800">
              {editingCode ? `Edit Coupon: ${editingCode}` : 'Create New Coupon'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <div className="md:col-span-1">
                <Label className="text-xs md:text-sm">Coupon Code *</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="E.g. JULY2026"
                  className="uppercase tracking-wider font-semibold"
                  disabled={!!editingCode}
                />
                {editingCode && <p className="text-[10px] text-slate-500 mt-1">Code cannot be changed after creation.</p>}
              </div>

              <div className="md:col-span-1">
                <Label className="text-xs md:text-sm">Title (banner heading)</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="🎉 Special Offer"
                />
              </div>

              <div className="md:col-span-2">
                <Label className="text-xs md:text-sm">Description</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Get Basic Plan at just ₹9 (Save ₹490)"
                />
              </div>

              <div>
                <Label className="text-xs md:text-sm">Discount Type</Label>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="fixed">Fixed Price (₹)</option>
                  <option value="percent">Percent Off (%)</option>
                </select>
                <p className="text-[10px] text-slate-500 mt-1">
                  {form.type === 'fixed' ? 'Final price after discount (e.g. 99)' : 'Discount percentage (e.g. 50)'}
                </p>
              </div>

              <div>
                <Label className="text-xs md:text-sm">
                  {form.type === 'fixed' ? 'Final Price (₹) *' : 'Percent Off (%) *'}
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={form.discountedPrice}
                  onChange={(e) => setForm({ ...form, discountedPrice: e.target.value })}
                  placeholder="99"
                />
              </div>

              <div className="md:col-span-2">
                <Label className="text-xs md:text-sm">Applies To Plan(s) *</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {PLAN_OPTIONS.map((plan) => {
                    const selected = form.appliesTo.includes(plan);
                    return (
                      <button
                        type="button"
                        key={plan}
                        onClick={() => togglePlan(plan)}
                        className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-semibold border-2 transition-all ${
                          selected
                            ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
                        }`}
                      >
                        {selected && <Check className="w-3 h-3 inline mr-1" />}
                        {plan}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="md:col-span-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="w-4 h-4 text-blue-600"
                />
                <Label htmlFor="active" className="text-xs md:text-sm cursor-pointer">
                  Active (currently visible to users)
                </Label>
              </div>

              <div className="md:col-span-2 flex flex-col-reverse sm:flex-row gap-2 pt-2">
                <Button type="button" variant="outline" onClick={cancelForm} className="w-full sm:w-auto">
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="w-full sm:flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                  {submitting ? 'Saving...' : editingCode ? 'Update Coupon' : 'Create Coupon'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Coupons List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {loading && coupons.length === 0 && (
          <div className="md:col-span-3 text-center py-8 text-slate-500 text-sm">Loading coupons...</div>
        )}
        {!loading && coupons.length === 0 && (
          <div className="md:col-span-3 text-center py-12 bg-white border border-dashed border-slate-200 rounded-xl">
            <Tag className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 text-sm">No coupons yet. Create your first offer campaign!</p>
          </div>
        )}
        {coupons.map((coupon) => (
          <Card
            key={coupon.code}
            className={`overflow-hidden border-2 ${
              coupon.active ? 'border-blue-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-75'
            }`}
          >
            <CardContent className="p-4 md:p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <code
                      onClick={() => handleCopyCode(coupon.code)}
                      className="px-2.5 py-1 bg-orange-100 text-orange-700 rounded-md font-mono font-extrabold text-sm tracking-wider cursor-pointer hover:bg-orange-200 transition-colors inline-flex items-center gap-1"
                    >
                      {coupon.code}
                      <Copy className="w-3 h-3 opacity-50" />
                    </code>
                    {coupon.active ? (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px] font-bold">ACTIVE</span>
                    ) : (
                      <span className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full text-[10px] font-bold">DISABLED</span>
                    )}
                  </div>
                  <h3 className="text-sm md:text-base font-bold text-slate-800 line-clamp-1">{coupon.title}</h3>
                  <p className="text-[11px] md:text-xs text-slate-500 line-clamp-2 mt-0.5">{coupon.description}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-3 text-[11px] md:text-xs">
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-slate-500">Discount</p>
                  <p className="font-bold text-slate-800">
                    {coupon.type === 'fixed' ? `₹${coupon.discountedPrice}` : `${coupon.discountedPrice}% OFF`}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-slate-500">Type</p>
                  <p className="font-bold text-slate-800 capitalize">{coupon.type}</p>
                </div>
              </div>

              <div className="mb-3">
                <p className="text-[10px] md:text-xs text-slate-500 mb-1">Applies to:</p>
                <div className="flex flex-wrap gap-1">
                  {(coupon.appliesTo || []).map((plan) => (
                    <span key={plan} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-semibold">
                      {plan}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex gap-1.5 pt-2 border-t border-slate-100">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleToggleActive(coupon)}
                  className="flex-1 text-xs"
                >
                  {coupon.active ? <X className="w-3.5 h-3.5 mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                  {coupon.active ? 'Disable' : 'Enable'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEdit(coupon)}
                  className="flex-1 text-xs"
                >
                  <Edit2 className="w-3.5 h-3.5 mr-1" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(coupon)}
                  className="text-xs text-red-600 hover:bg-red-50 hover:border-red-300"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default CouponsManagement;
